import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, RotateCcw } from "lucide-react";
import { rbacApi, usersApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";

const ROLE_LABELS = { admin: "Administrator", manager: "Manager", member: "Mitarbeiter", readonly: "Nur Lesen" };

export function RbacMatrixDialog({ open, onOpenChange, user, onSaved }) {
  const toast = useToast();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [effective, setEffective] = useState(new Set());

  const basePerms = useMemo(() => {
    if (!meta || !user) return new Set();
    return new Set(meta.role_base_permissions?.[user.role] || []);
  }, [meta, user]);

  const initEffective = useCallback(
    (metaData) => {
      const base = new Set(metaData.role_base_permissions?.[user.role] || []);
      const override = new Set(user.permissions_override || []);
      const revoked = new Set(user.revoked_permissions || []);
      const eff = new Set([...base, ...override]);
      revoked.forEach((p) => eff.delete(p));
      setEffective(eff);
    },
    [user]
  );

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    rbacApi
      .getMeta()
      .then((res) => {
        setMeta(res.data);
        initEffective(res.data);
      })
      .catch(() => toast.error("Rechte-Metadaten konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [open, user, initEffective, toast]);

  const toggle = (perm) => {
    setEffective((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const resetToRole = () => setEffective(new Set(basePerms));

  const handleSave = async () => {
    setSaving(true);
    try {
      const override = [...effective].filter((p) => !basePerms.has(p));
      const revoked = [...basePerms].filter((p) => !effective.has(p));
      await usersApi.updatePermissions(user.id, {
        permissions_override: override,
        revoked_permissions: revoked,
      });
      toast.success("Rechte gespeichert");
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rechte konnten nicht gespeichert werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Rechte verwalten
          </DialogTitle>
          <DialogDescription>
            Granulare Berechtigungen für <span className="font-medium">{user?.full_name}</span> ·{" "}
            Rolle: {ROLE_LABELS[user?.role] || "Mitarbeiter"}. Häkchen oberhalb der Rollen-Standardrechte
            gewähren zusätzliche Rechte, entfernte Häkchen entziehen Standardrechte.
          </DialogDescription>
        </DialogHeader>

        {loading || !meta ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[55vh]">
            <table className="w-full text-sm border-collapse" data-testid="rbac-matrix">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-3 sticky left-0 bg-background font-medium">Modul</th>
                  {meta.actions.map((a) => (
                    <th key={a.key} className="px-2 py-2 text-center font-medium whitespace-nowrap">
                      {a.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meta.modules.map((m) => (
                  <tr key={m.key} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-medium sticky left-0 bg-background">{m.label}</td>
                    {meta.actions.map((a) => {
                      const perm = `${m.key}:${a.key}`;
                      const isBase = basePerms.has(perm);
                      const isOn = effective.has(perm);
                      return (
                        <td key={a.key} className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Checkbox
                              checked={isOn}
                              onCheckedChange={() => toggle(perm)}
                              data-testid={`rbac-cell-${perm}`}
                            />
                            {isBase && (
                              <span className="text-[9px] leading-none text-muted-foreground">Rolle</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          <Button type="button" variant="ghost" onClick={resetToRole} disabled={loading} data-testid="rbac-reset-btn">
            <RotateCcw className="w-4 h-4 mr-2" />
            Auf Rollen-Standard zurücksetzen
          </Button>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || loading} data-testid="rbac-save-btn">
              {saving ? "Speichern…" : "Rechte speichern"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
